import { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import type { DemoTourStep } from './storyboard';

interface DemoTourOverlayProps {
  step: DemoTourStep;
  stepIndex: number;
  totalSteps: number;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
}

interface HighlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export function DemoTourOverlay({
  step,
  stepIndex,
  totalSteps,
  onNext,
  onPrev,
  onSkip,
}: DemoTourOverlayProps) {
  const [highlightRect, setHighlightRect] = useState<HighlightRect | null>(null);
  const retryRef = useRef<ReturnType<typeof setTimeout>>();

  const measure = useCallback(() => {
    if (!step.highlightSelector) {
      setHighlightRect(null);
      return;
    }
    const el = document.querySelector(step.highlightSelector);
    if (el) {
      const rect = el.getBoundingClientRect();
      const padding = 8;
      setHighlightRect({
        top: rect.top - padding + window.scrollY,
        left: rect.left - padding,
        width: rect.width + padding * 2,
        height: rect.height + padding * 2,
      });
    } else {
      setHighlightRect(null);
    }
  }, [step.highlightSelector]);

  // Measure on mount and after a short delay for lazy-rendered content
  useEffect(() => {
    measure();
    // Retry a few times for elements that render asynchronously
    const delays = [100, 300, 600, 1200];
    const timers = delays.map((d) => setTimeout(measure, d));
    retryRef.current = timers[timers.length - 1];
    return () => timers.forEach(clearTimeout);
  }, [measure, stepIndex]);

  // Re-measure on scroll/resize
  useEffect(() => {
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [measure]);

  const isFirst = stepIndex === 0;
  const isLast = stepIndex === totalSteps - 1;

  // Fullscreen overlay (welcome / CTA)
  if (step.fullscreen) {
    return createPortal(
      <AnimatePresence>
        <motion.div
          key={`fullscreen-${stepIndex}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[99999] flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.82)' }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.4, ease: 'easeOut' }}
            className="max-w-md w-full mx-6 rounded-2xl p-8 text-center"
            style={{
              background: 'linear-gradient(145deg, #0f1419 0%, #1a2332 100%)',
              border: '1px solid rgba(16, 185, 129, 0.25)',
              boxShadow: '0 0 60px rgba(16, 185, 129, 0.12)',
            }}
          >
            {step.eyebrow && (
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.24em] text-emerald-400/80">
                {step.eyebrow}
              </div>
            )}
            <h2 className="text-2xl font-bold text-white mb-3">{step.title}</h2>
            <p className="text-gray-400 text-sm leading-relaxed mb-8">{step.description}</p>
            <button
              onClick={onNext}
              className="px-8 py-3 rounded-xl text-sm font-semibold text-white transition-all"
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                boxShadow: '0 4px 20px rgba(16, 185, 129, 0.35)',
              }}
            >
              {step.nextLabel || (isFirst ? '开始体验' : isLast ? '创建你的 Agent' : '下一步')}
            </button>
            {isFirst && (
              <button
                onClick={onSkip}
                className="block mx-auto mt-4 text-xs text-gray-500 hover:text-gray-300 transition-colors"
              >
                跳过引导
              </button>
            )}
          </motion.div>
        </motion.div>
      </AnimatePresence>,
      document.body,
    );
  }

  // Highlight mode
  const cardPosition = getCardPosition(step.position, highlightRect);

  return createPortal(
    <AnimatePresence>
      <motion.div
        key={`highlight-${stepIndex}`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[99999]"
        style={{ pointerEvents: 'auto' }}
      >
        {/* Overlay mask with cutout */}
        <svg className="absolute inset-0 w-full h-full" style={{ minHeight: '100vh' }}>
          <defs>
            <mask id="tour-mask">
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              {highlightRect && (
                <rect
                  x={highlightRect.left}
                  y={highlightRect.top}
                  width={highlightRect.width}
                  height={highlightRect.height}
                  rx="12"
                  fill="black"
                />
              )}
            </mask>
          </defs>
          <rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            fill="rgba(0,0,0,0.6)"
            mask="url(#tour-mask)"
          />
        </svg>

        {/* Highlight border glow */}
        {highlightRect && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute rounded-xl pointer-events-none"
            style={{
              top: highlightRect.top,
              left: highlightRect.left,
              width: highlightRect.width,
              height: highlightRect.height,
              border: '2px solid rgba(16, 185, 129, 0.5)',
              boxShadow: '0 0 20px rgba(16, 185, 129, 0.2), inset 0 0 20px rgba(16, 185, 129, 0.05)',
            }}
          />
        )}

        {/* Info card */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="absolute px-4 w-full"
          style={cardPosition}
          >
            <div
              className="max-w-sm mx-auto rounded-2xl p-5"
            style={{
              background: 'linear-gradient(145deg, #0f1419 0%, #1a2332 100%)',
              border: '1px solid rgba(16, 185, 129, 0.2)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            }}
          >
            {step.eyebrow && (
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-400/80 mb-2">
                {step.eyebrow}
              </p>
            )}
            <h3 className="text-lg font-bold text-white mb-2">{step.title}</h3>
            <p className="text-gray-400 text-sm leading-relaxed">{step.description}</p>
          </div>
        </motion.div>

        {/* Bottom controls */}
        <div
          className="fixed bottom-0 left-0 right-0 px-4 py-4 flex items-center justify-between"
          style={{
            background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 100%)',
          }}
        >
          <button
            onClick={onSkip}
            className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
          >
            跳过
          </button>

          {/* Step dots */}
          <div className="flex items-center gap-1.5">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className="rounded-full transition-all duration-300"
                style={{
                  width: i === stepIndex ? 16 : 6,
                  height: 6,
                  background:
                    i === stepIndex
                      ? '#10b981'
                      : i < stepIndex
                        ? 'rgba(16, 185, 129, 0.4)'
                        : 'rgba(255,255,255,0.2)',
                }}
              />
            ))}
          </div>

          <div className="flex gap-2">
            {stepIndex > 0 && !isFirst && (
              <button
                onClick={onPrev}
                className="px-4 py-2 rounded-lg text-xs font-medium text-gray-300 border border-gray-600 hover:border-gray-400 transition-colors"
              >
                上一步
              </button>
            )}
            <button
              onClick={onNext}
              className="px-4 py-2 rounded-lg text-xs font-semibold text-white transition-all"
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              }}
            >
              {step.nextLabel || (isLast ? '完成' : '下一步')}
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>,
    document.body,
  );
}

function getCardPosition(
  position: DemoTourStep['position'],
  rect: HighlightRect | null,
): React.CSSProperties {
  if (!rect) {
    return { top: '50%', left: 0, right: 0, transform: 'translateY(-50%)' };
  }

  const viewportHeight = window.innerHeight;
  const spaceBelow = viewportHeight - (rect.top + rect.height);
  const spaceAbove = rect.top;
  const cardHeight = 120;

  if (position === 'top' || (!position && spaceAbove > spaceBelow && spaceAbove > cardHeight + 20)) {
    return { top: rect.top - cardHeight - 16, left: 0, right: 0 };
  }

  if (position === 'bottom' || (!position && spaceBelow > cardHeight + 20)) {
    return { top: rect.top + rect.height + 16, left: 0, right: 0 };
  }

  // Fallback: center
  return { top: '50%', left: 0, right: 0, transform: 'translateY(-50%)' };
}
